/**
 * Pulpit portalu (spec 03 §1) — hero, trzy KPI i skrót do wniosków.
 *
 * Wszystkie liczby pochodzą z realnych danych pracownika. Kafel wypłaty pokazuje
 * kwotę DO WYPŁATY (po potrąceniach, tą samą regułą co plik przelewów)
 * z najbliższego terminu; bez przyszłego terminu — ostatnią wypłatę pod
 * napisem „OSTATNIA WYPŁATA", a z listy w weryfikacji — kwotę wstępną (D108).
 * Dopóki żadna lista płac nie została policzona, mówi „brak danych" zamiast
 * kwoty z makiety [D19].
 */
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  EmptyState,
  InfoBanner,
  Inbox,
  Spinner,
  StatCard,
  useToast,
} from '@k1p4/ui'
import { formatPLN } from '@k1p4/i18n'
import { api, apiToday, openFile, type PortalDashboardResponse } from '../../api/client'
import { formatCount, pluralUnit } from './units'
import styles from './portal.module.css'
import { greetingName, useFormat } from '../../format'

/** Wartość zmiany warunków: kwoty w groszach czytamy jak kwoty, resztę wprost. */
function changeValue(field: string, value: string | null): string {
  if (value === null) return '—'
  if (field.endsWith('Gr')) return formatPLN(Number(value))
  return value
}

export function PortalHome() {
  const fmt = useFormat()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()

  /*
   * Nota kafla wypłaty: data, a gdy coś potrącono — od jakiego netto; przy
   * liście w weryfikacji dopisek „kwota wstępna" (D108).
   */
  function payoutNote(payout: PortalDashboardResponse['nextPayout']): string {
    if (!payout) return t('portal.home.kpiPayoutNote')
    const parts: string[] = []
    if (payout.date) {
      const date = fmt.date(payout.date, 'long')
      parts.push(
        payout.payoutGr === payout.netGr
          ? t('portal.home.kpiPayoutDate', { date })
          : t('portal.home.kpiPayoutAfterDeductions', { date, net: formatPLN(payout.netGr) }),
      )
    }
    if (payout.draft) parts.push(t('portal.home.kpiPayoutDraft'))
    return parts.join(' · ')
  }

  const dashboard = useQuery({ queryKey: ['me', 'dashboard'], queryFn: () => api.meDashboard() })
  /* Aneks czekający na decyzję pracownika (Etap 14) — porozumienie stron. */
  const terms = useQuery({
    queryKey: ['me', 'terms-change'],
    queryFn: () => api.portalTermsChange(),
  })
  const decide = useMutation({
    mutationFn: (decision: 'accept' | 'reject') => api.decideTermsChange(decision),
    onSuccess: (_result, decision) => {
      /*
       * Przyjęcie aneksu nie tworzy dokumentu (ADR-0015), więc komunikat mówi,
       * od kiedy obowiązują warunki, a nie że coś „trafiło do akt" (grupa 138).
       */
      const startDate = terms.data?.proposal?.startDate
      toast.show(
        t(`portal.terms.${decision === 'accept' ? 'accepted' : 'rejected'}`, {
          date: startDate ? fmt.date(startDate, 'long') : '—',
        }),
        { tone: decision === 'accept' ? 'success' : 'info' },
      )
      void queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })

  if (dashboard.isPending) return <Spinner label={t('common.loading')} />
  if (dashboard.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const data = dashboard.data

  /* Wołacz po polsku, a imię spoza słownika — powitanie bez imienia (grupa 162). */

  const greeting = greetingName(fmt.lng, data.employee?.firstName)
  if (!data.hasEmployeeRecord) {
    return (
      <EmptyState
        icon={<Inbox size={28} />}
        title={t('portal.noRecord')}
        description={t('portalRequests.emptyBody')}
      />
    )
  }

  const year = data.year ?? apiToday().getFullYear()
  const remaining = data.leave?.remainingDays ?? 0
  /*
   * Kafel urlopu z tej samej puli co karta urlopów (Fala 7, grupy 24 i 35):
   * przy umowie cywilnej „nie dotyczy" zamiast zera (art. 152 § 1 k.p., D102),
   * a nota podaje limit w dniach i godzinach.
   */
  const leaveNotApplicable = data.leaveEntitlement === 'nie_dotyczy' || !data.leave
  const leaveLimit = data.leave?.limitDays ?? null
  const leaveHours = data.leave?.limitHours ?? null
  const toSign = data.documentsToSign ?? 0
  const pendingLeaves = data.pendingLeaves ?? 0
  const pendingRequests = data.pendingRequests ?? 0

  return (
    <>
      <section className={styles.hero}>
        <span className={styles.heroBadge}>
          <span className={styles.heroDot} aria-hidden="true" />
          {t('portal.home.badge')}
        </span>
        <h2 className={styles.heroTitle}>
          {greeting === null
            ? t('portal.home.greetingNeutral')
            : t('portal.home.greeting', { name: greeting })}
        </h2>
        <p className={styles.heroLead}>
          {toSign > 0 ? `${t('portal.home.leadWithDocs')} ` : ''}
          {t('portal.home.lead')}
        </p>
      </section>

      <div className={styles.kpiGrid}>
        {leaveNotApplicable ? (
          <StatCard
            label={t('portal.home.kpiLeave')}
            value={t('portal.home.kpiLeaveNotApplicable')}
            note={t('portal.home.kpiLeaveNotApplicableNote')}
          />
        ) : data.employment?.ended === true && data.leaveEquivalentPaid === true ? (
          /* Po ustaniu urlop wypłacony jako ekwiwalent — nie ma już dni do wykorzystania (fala 9b). */
          <StatCard
            label={t('portal.home.kpiLeave')}
            value={t('portal.home.kpiLeaveSettled')}
            note={t('portal.home.kpiLeaveSettledNote')}
          />
        ) : (
          <StatCard
            label={t('portal.home.kpiLeave')}
            value={formatCount(i18n.language, remaining)}
            unit={pluralUnit(i18n.language, remaining, t, 'day')}
            note={
              leaveLimit
                ? leaveHours !== null
                  ? t('portal.home.kpiLeaveNoteHours', { count: leaveLimit, hours: leaveHours, year })
                  : t('portal.home.kpiLeaveNote', { count: leaveLimit, year })
                : undefined
            }
          />
        )}
        <StatCard
          label={t(
            data.nextPayout && !data.nextPayout.upcoming
              ? 'portal.home.kpiPayoutLast'
              : 'portal.home.kpiPayout',
          )}
          value={
            data.nextPayout ? formatPLN(data.nextPayout.payoutGr) : t('portal.home.kpiPayoutNone')
          }
          note={payoutNote(data.nextPayout)}
          {...(data.nextPayout ? { onClick: () => navigate('/me/pay') } : {})}
        />
        <StatCard
          label={t('portal.home.kpiToSign')}
          value={toSign > 0 ? toSign : t('portal.home.kpiToSignNone')}
          unit={toSign > 0 ? pluralUnit(i18n.language, toSign, t, 'document') : undefined}
          note={toSign > 0 ? t('portal.home.kpiToSignNote') : undefined}
          highlighted={toSign > 0}
          onClick={() => navigate('/me/documents')}
        />
      </div>

      {terms.data?.proposal ? (
        <Card pad="lg" className={styles.termsCard}>
          <h3 className={styles.termsTitle}>{t('portal.terms.title')}</h3>
          <p className={styles.termsLead}>
            {t('portal.terms.lead', {
              date: terms.data.proposal.startDate
                ? fmt.date(terms.data.proposal.startDate, 'long')
                : '—',
            })}
          </p>
          <table className={styles.termsTable}>
            <thead>
              <tr>
                <th>{t('portal.terms.field')}</th>
                <th>{t('portal.terms.before')}</th>
                <th>{t('portal.terms.after')}</th>
              </tr>
            </thead>
            <tbody>
              {terms.data.proposal.changes.map((change) => (
                <tr key={change.field}>
                  <td>{t(`portal.terms.fields.${change.field}`)}</td>
                  <td>{changeValue(change.field, change.before)}</td>
                  <td>{changeValue(change.field, change.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.termsActions}>
            <Button
              variant="secondary"
              disabled={decide.isPending}
              onClick={() => decide.mutate('reject')}
            >
              {t('portal.terms.reject')}
            </Button>
            <Button disabled={decide.isPending} onClick={() => decide.mutate('accept')}>
              {t('portal.terms.accept')}
            </Button>
          </div>
        </Card>
      ) : null}

      {/*
        Kopia własnych danych (art. 15 RODO, Etap 20). Plik idzie przez
        nawigację — pełny PESEL nie przechodzi przez stan aplikacji.
      */}
      <Card pad="lg" className={styles.termsCard}>
        <h3 className={styles.termsTitle}>{t('portal.dataExport.title')}</h3>
        <p className={styles.termsLead}>{t('portal.dataExport.lead')}</p>
        <div className={styles.termsActions}>
          <Button
            variant="secondary"
            onClick={() => {
              openFile(api.myDataExportUrl())
            }}
          >
            {t('portal.dataExport.action')}
          </Button>
        </div>
      </Card>

      {/*
        Wniosek urlopowy czeka na przełożonego z akt, a bez niego — na kadry;
        pozostałe wnioski rozstrzyga dział HR. Dawniej baner mówił
        o przełożonym zawsze, także przy samych wnioskach nieurlopowych
        (Fala 7, grupa 111).
      */}
      {pendingLeaves > 0 ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t(
            data.leaveApprover === 'hr'
              ? 'assistant.hints.myLeavePendingHr'
              : 'assistant.hints.myLeavePending',
          )}
        </InfoBanner>
      ) : null}
      {pendingRequests > 0 ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('assistant.hints.myRequestPending', { count: pendingRequests })}
        </InfoBanner>
      ) : null}
    </>
  )
}
